import { useField } from '@unform/core';
import { AutocompleteField, ErrorMessage } from 'components';
import React, { ComponentProps, memo, useEffect, useRef, useState } from 'react';

import * as Styles from './styles';

interface IProps extends ComponentProps<typeof AutocompleteField> {
  name: string;
  notErrorMargin?: boolean;
  returnAllOption?: boolean;
}

const AutocompleteInputComponent: React.FC<IProps> = ({
  name,
  notErrorMargin,
  onSelectOption,
  returnAllOption,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { fieldName, defaultValue, registerField, error, clearError } = useField(name);
  const [value, setValue] = useState<null | string>(defaultValue || null);
  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,
      getValue: () => {
        if (returnAllOption) {
          if (!value) return null;

          const selectedOption = props.options.find(
            option =>
              option[props.optionValue || 'value'] ===
              (typeof value === 'object' ? value[props.optionValue || value] : value),
          );

          if (selectedOption === undefined) return null;

          return selectedOption;
        }

        if (typeof value === 'number') {
          return value;
        }

        if (typeof value === 'object' && value) {
          return value[props.optionValue || 'value'];
        }

        return typeof value === 'undefined' ? null : value;
      },
      setValue: (ref, value) => {
        ref.current.value = value;
      },
      clearValue: ref => {
        ref.current.value = '';
      },
    });
  }, [
    fieldName,
    registerField,
    inputRef,
    value,
    returnAllOption,
    props.options,
    props.optionValue,
  ]);
  return (
    <Styles.Container>
      <AutocompleteField
        {...props}
        inputRef={inputRef}
        isInvalid={!!error}
        onSelectOption={value => {
          onSelectOption?.(value);

          setValue(value.optionValue);
          error && clearError();
        }}
        defaultValue={defaultValue}
      />

      {notErrorMargin ? (
        error && <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <ErrorMessage>{error}</ErrorMessage>
      )}
    </Styles.Container>
  );
};

export const AutocompleteInput = memo(AutocompleteInputComponent);
